import { Box, Checkbox, Loader, Stack, Text } from '@mantine/core';
import { publicMessageOf } from '../lib/trpc';
import classes from './DataTable.module.css';

export interface Column<T> {
  key: string;
  header: string;
  align?: 'left' | 'right';
  render: (row: T) => React.ReactNode;
}

/**
 * Row selection, the input to the mockup's «МАСОВІ ДІЇ · N САЙТІВ» bar.
 *
 * The selection lives with the caller, not here: it has to survive a refetch, and on the
 * sites registry it is what gets handed to the bulk screen.
 */
export interface DataTableSelection<T> {
  idOf: (row: T) => string;
  selected: ReadonlySet<string>;
  onChange: (next: Set<string>) => void;
}

/**
 * Sorting used to live here as a `sort` prop and a `sortKey` on the column, with no registry
 * turning it on. It is gone rather than wired up: the mockup that defines this product has no
 * sortable header anywhere — no arrows, no `aria-sort`, no clickable `th`. Its one mention of
 * ordering (line 3668, «сортування: вручну (drag)») is about dragging elements inside a
 * content block, not about a registry column. Building a control the specification does not
 * ask for is how the gap this component was meant to close got here in the first place.
 * Server-side ordering by a chosen key stays available in `paginationInput`.
 */
export interface DataTableProps<T> {
  rows: T[];
  columns: Column<T>[];
  loading?: boolean;
  /**
   * A failed query must never render as "no rows". Pass the query's error through so the
   * table says the request failed instead of quietly implying the table is empty.
   */
  error?: unknown;
  /** Empty states carry a CTA, as in the mockup — never a blank rectangle. */
  emptyMessage: string;
  onRowClick?: (row: T) => void;
  rowTestId?: (row: T) => string;
  testId?: string;
  selection?: DataTableSelection<T>;
  /** Left side of the footer — «1–100 з 5 214», as the mockup writes it. */
  footnote?: React.ReactNode;
  /** Right side of the footer: pagination controls, «Експорт CSV», «Зберегти вʼю». */
  footerActions?: React.ReactNode;
}

/**
 * The dense registry table from the mockup.
 *
 * Deliberately not virtualised — that arrives with the sites registry, where 5 214 rows
 * make it necessary. Reference lists are tens of rows, and a virtualiser there would be
 * complexity with nothing to buy.
 *
 * Selection and the footer are both optional: eleven screens use this component and most of
 * them are catalogues where a checkbox column would be noise. The mockup draws checkboxes on
 * the sites registry and nowhere else.
 */
export function DataTable<T>({
  rows,
  columns,
  loading = false,
  error,
  emptyMessage,
  onRowClick,
  rowTestId,
  testId,
  selection,
  footnote,
  footerActions,
}: DataTableProps<T>): React.JSX.Element {
  if (loading) {
    return (
      <Box className={classes.state} data-testid={testId ? `${testId}-loading` : undefined}>
        <Loader size="sm" />
      </Box>
    );
  }

  if (error) {
    return (
      <Box className={classes.errorState} data-testid={testId ? `${testId}-error` : undefined}>
        <Stack gap={6} align="center">
          <Text className={classes.errorTitle}>Не вдалося завантажити</Text>
          <Text className={classes.emptyBody}>{publicMessageOf(error)}</Text>
        </Stack>
      </Box>
    );
  }

  if (rows.length === 0) {
    return (
      <Box className={classes.state} data-testid={testId ? `${testId}-empty` : undefined}>
        <Stack gap={6} align="center">
          <Text className={classes.emptyTitle}>Порожньо</Text>
          <Text className={classes.emptyBody}>{emptyMessage}</Text>
        </Stack>
      </Box>
    );
  }

  const ids = selection ? rows.map((row) => selection.idOf(row)) : [];
  const selectedHere = ids.filter((id) => selection?.selected.has(id) === true);
  const allSelected = ids.length > 0 && selectedHere.length === ids.length;

  const toggleAll = (): void => {
    if (!selection) return;
    const next = new Set(selection.selected);
    // Toggling the header clears or fills only what is on screen. Silently dropping a
    // selection the operator made on another page is how a bulk run loses half its targets.
    if (allSelected) for (const id of ids) next.delete(id);
    else for (const id of ids) next.add(id);
    selection.onChange(next);
  };

  const toggleRow = (id: string): void => {
    if (!selection) return;
    const next = new Set(selection.selected);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    selection.onChange(next);
  };

  return (
    <Box className={classes.wrapper} data-testid={testId}>
      <table className={classes.table} data-selectable={selection ? true : undefined}>
        <thead>
          <tr>
            {selection ? (
              <th className={[classes.th, classes.selectCell].join(' ')}>
                <Checkbox
                  checked={allSelected}
                  indeterminate={selectedHere.length > 0 && !allSelected}
                  onChange={toggleAll}
                  aria-label="Вибрати всі рядки на сторінці"
                  data-testid={testId ? `${testId}-select-all` : undefined}
                />
              </th>
            ) : null}
            {columns.map((column, index) => (
              <th
                key={column.key}
                // The first data column stays put while the rest scrolls sideways: below
                // 1280 the registries do scroll, and a row you cannot identify is unusable.
                className={[classes.th, index === 0 ? classes.stickyCell : ''].filter(Boolean).join(' ')}
                style={column.align === 'right' ? { textAlign: 'right' } : undefined}
              >
                {column.header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, index) => {
            const id = selection?.idOf(row);
            const isSelected = id !== undefined && selection?.selected.has(id) === true;
            return (
              <tr
                key={rowTestId?.(row) ?? String(index)}
                className={classes.tr}
                data-clickable={onRowClick ? true : undefined}
                data-selected={isSelected || undefined}
                data-testid={rowTestId?.(row)}
                onClick={onRowClick ? () => { onRowClick(row); } : undefined}
                // A clickable row must be reachable without a mouse.
                tabIndex={onRowClick ? 0 : undefined}
                onKeyDown={
                  onRowClick
                    ? (event) => {
                        if (event.key === 'Enter' || event.key === ' ') {
                          event.preventDefault();
                          onRowClick(row);
                        }
                      }
                    : undefined
                }
              >
                {selection && id !== undefined ? (
                  <td
                    className={[classes.td, classes.selectCell].join(' ')}
                    // Without this the checkbox also opens the row — the operator ticks a
                    // box to include a site in a bulk run and lands on its card instead.
                    onClick={(event) => { event.stopPropagation(); }}
                  >
                    <Checkbox
                      checked={isSelected}
                      onChange={() => { toggleRow(id); }}
                      aria-label={`Вибрати рядок ${id}`}
                      data-testid={rowTestId ? `${rowTestId(row)}-select` : undefined}
                    />
                  </td>
                ) : null}
                {columns.map((column, columnIndex) => (
                  <td
                    key={column.key}
                    className={[classes.td, columnIndex === 0 ? classes.stickyCell : ''].filter(Boolean).join(' ')}
                    style={column.align === 'right' ? { textAlign: 'right' } : undefined}
                  >
                    {column.render(row)}
                  </td>
                ))}
              </tr>
            );
          })}
        </tbody>
      </table>

      {footnote === undefined && footerActions === undefined && selection === undefined ? null : (
        <Box className={classes.footer} data-testid={testId ? `${testId}-footer` : undefined}>
          <span className={classes.footerCount}>
            {selection && selection.selected.size > 0
              ? `вибрано ${String(selection.selected.size)}`
              : footnote}
          </span>
          {footerActions}
        </Box>
      )}
    </Box>
  );
}
